import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBullhorn } from '@fortawesome/free-solid-svg-icons';
import axiosInstance from '../../api/axiosInstance';
import { API_ENDPOINTS } from '../../api/config';
import { useProfile } from '../../context/ProfileContext';
import './AnnouncementMarquee.css';

interface MarqueeAnnouncement {
  id: number;
  title: string;
  message: string;
  priority: string;
}

const truncate = (text: string, max: number) =>
  text.length > max ? `${text.slice(0, max - 1).trimEnd()}…` : text;

const AnnouncementMarquee: React.FC = () => {
  const { t } = useTranslation();
  const { activeProfile } = useProfile();
  const [announcements, setAnnouncements] = useState<MarqueeAnnouncement[]>([]);

  useEffect(() => {
    let cancelled = false;
    const fetchAnnouncements = async () => {
      try {
        const res = await axiosInstance.get(API_ENDPOINTS.myAnnouncements);
        const data = Array.isArray(res.data) ? res.data : res.data.results || [];
        if (!cancelled) setAnnouncements(data);
      } catch {
        if (!cancelled) setAnnouncements([]);
      }
    };
    fetchAnnouncements();
    return () => {
      cancelled = true;
    };
  }, [activeProfile]);

  if (announcements.length === 0) {
    return null;
  }

  const priorityColor: Record<string, string> = {
    low: '#e2e8f0',
    normal: '#a5f3fc',
    high: '#fde047',
    urgent: '#fda4af',
  };

  const trackItems = [...announcements, ...announcements];
  const duration = Math.max(20, announcements.length * 8);

  return (
    <div className="ann-marquee" role="region" aria-label={t('home:announcements.title')}>
      <div className="ann-marquee-label">
        <FontAwesomeIcon icon={faBullhorn} />
        <span>{t('home:announcements.title')}</span>
      </div>
      <div className="ann-marquee-viewport">
        <div className="ann-marquee-track" style={{ animationDuration: `${duration}s` }}>
          {trackItems.map((ann, idx) => (
            <span className="ann-marquee-item" key={`${ann.id}-${idx}`}>
              <span
                className="ann-marquee-dot"
                style={{ background: priorityColor[ann.priority] || '#ffffff' }}
              />
              <strong>{truncate(ann.title, 80)}</strong>
            </span>
          ))}
        </div>
      </div>
    </div>
  );
};

export default AnnouncementMarquee;